import React, { useState, useEffect } from 'react';
import { EmptyState } from '../platform/EmptyState';

type DinePaymentMethod = {
  name: string;
  availability: Record<string, string>;
};

type DinePaymentsData = {
  countries: string[];
  methods: DinePaymentMethod[];
};

type DinePaymentsTableProps = {
  endpoint?: string;
};

const COLUMN_WIDTH = '45px';

function renderCell(value: string) {
  if (value === 'TRUE') {
    return <span className="text-green-600 font-semibold">✓</span>;
  }
  if (value === 'X') {
    return <span className="text-red-500 font-semibold">✗</span>;
  }
  if (value === 'N/A') {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return (
    <span className="inline-block px-1 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-800 leading-tight whitespace-nowrap">
      {value}
    </span>
  );
}

export function DinePaymentsTable({ endpoint = '/api/dine-payments' }: DinePaymentsTableProps) {
  const [data, setData] = useState<DinePaymentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setLoading(true);
    setError(null);
    fetch(endpoint)
      .then(r => r.json())
      .then(json => {
        if (json.success) {
          setData({ countries: json.countries, methods: json.methods });
        } else {
          setError(json.error || 'Failed to load data');
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [endpoint]);

  if (loading) {
    return <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>;
  }

  if (error || !data) {
    return (
      <div className="py-8 text-center text-red-500 text-sm">
        {error || 'No data available'}
      </div>
    );
  }

  if (data.methods.length === 0) {
    return <EmptyState message="Currently there is nothing to show here" />;
  }

  return (
    <div className="space-y-2">
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full min-w-[600px]" style={{ tableLayout: 'fixed' }}>
        <thead className="bg-white dark:bg-gray-900">
          <tr className="h-20 border-b border-border">
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground bg-white dark:bg-gray-900"
              style={{ width: '200px' }}
            >
              Payment Method
            </th>
            {data.countries.map(country => (
              <th
                key={country}
                className="text-center px-1 py-2 font-medium text-foreground bg-white dark:bg-gray-900 cursor-default select-none"
                style={{ width: COLUMN_WIDTH }}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">
                    {country}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.methods.map((method, index) => (
            <tr
              key={method.name}
              className={`border-b border-border ${
                index === data.methods.length - 1 ? 'border-b-0' : ''
              }`}
            >
              <td
                className="p-4 font-medium text-foreground text-sm"
                style={{ width: '200px' }}
              >
                {method.name}
              </td>
              {data.countries.map(country => (
                <td
                  key={country}
                  className="p-4 text-center"
                  style={{ width: COLUMN_WIDTH }}
                >
                  {renderCell(method.availability[country] ?? 'X')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};
